import React, { useId, useMemo, useState } from 'react';
import { BereichKontext } from './Bausteine';
import { ErklaerFenster, InfoTipp, type ErklaerInhalt } from '../../components/Erklaerung';

// Das eine Gerüst jedes Bereichs der Einstellungen (AE-1 Nachtrag 3, SPEC AE-06; Gestaltung 08.10.2026, GESTALTUNG.md
// 2.6 und 2.7): ein kleiner, klebender Seitenkopf mit Titel, höchstens einer Beschreibungszeile und rechts der
// Hauptaktion, darunter Abschnitte ohne Karte. Kein Bereich bringt einen eigenen Seitenrahmen oder eine eigene Breite mit.
// Der Kontext sagt den Bausteinen, dass sie im Pop-up stehen, und nennt die Stelle für <Bereichsaktion>.
// F-24 (GRUNDREGELN 1e): erklaerung setzt den Link „Wie funktioniert …?“ rechts in den Kopf, vor die Hauptaktion; ohne sie bleibt
// die Ausgabe wie bisher.

export function Bereich({ titel, beschreibung, aktion, erklaerung, children }: { titel: string; beschreibung?: React.ReactNode; aktion?: React.ReactNode; erklaerung?: ErklaerInhalt; children?: React.ReactNode }) {
  const [stelle, setStelle] = useState<HTMLDivElement | null>(null);
  const kontext = useMemo(() => ({ aktionsStelle: stelle }), [stelle]);
  return <BereichKontext.Provider value={kontext}>
    <div className="einst-bereich">
      <header className="einst-kopf">
        <div><h1 tabIndex={-1}>{titel}</h1>{beschreibung && <p>{beschreibung}</p>}</div>
        <div className="einst-kopf-aktion" ref={setStelle}>{erklaerung && <ErklaerFenster {...erklaerung} />}{aktion}</div>
      </header>
      {children}
    </div>
  </BereichKontext.Provider>;
}

// Ein Abschnitt: kleine Überschrift (h2), optional graue Beschreibung und rechts Aktionen, darunter der Inhalt; keine
// Fläche. zustand steht vor der Überschrift (Mein Zugang: Symbol und Wort); label ersetzt den Namen aus der Überschrift;
// titelVersteckt lässt die Überschrift nur für Bildschirmleser stehen (wiederholt sie den Seitentitel).
// F-24 (GRUNDREGELN 1e): tipp setzt das Info-Symbol hinter den Titel, erklaerung den Link „Wie funktioniert …?“ rechts in den Kopf;
// ohne beide bleibt die Ausgabe wie bisher.
export function Abschnitt({ titel, beschreibung, aktionen, zustand, label, titelVersteckt, tipp, erklaerung, children }: { titel: React.ReactNode; beschreibung?: React.ReactNode;
  aktionen?: React.ReactNode; zustand?: React.ReactNode; label?: string; titelVersteckt?: boolean; tipp?: React.ReactNode; erklaerung?: ErklaerInhalt; children?: React.ReactNode }) {
  const id = useId();
  const nurVersteckt = titelVersteckt && !beschreibung && !aktionen && !zustand && !erklaerung;
  const ueberschrift = <h2 id={id} className={titelVersteckt ? 'adm-sr-only' : undefined}>{titel}</h2>;
  return <section className="einst-abschnitt" {...(label ? { 'aria-label': label } : { 'aria-labelledby': id })}>
    <div className={nurVersteckt ? 'einst-abschnitt-kopf einst-abschnitt-kopf--versteckt' : 'einst-abschnitt-kopf'}>
      <div className="einst-abschnitt-titel">{zustand}{tipp && !titelVersteckt
        ? <div className="erkl-titelzeile">{ueberschrift}<InfoTipp wort={typeof titel === 'string' ? titel : label ?? ''} text={tipp} /></div>
        : ueberschrift}{beschreibung && <p className="einst-abschnitt-text">{beschreibung}</p>}</div>
      {(aktionen || erklaerung) && <div className="einst-abschnitt-aktionen">{erklaerung && <ErklaerFenster {...erklaerung} />}{aktionen}</div>}
    </div>
    {children && <div className="einst-abschnitt-inhalt">{children}</div>}
  </section>;
}
